import { useState } from "react";
import { useT, translateError } from "../../i18n/hooks.ts";
import { mappingCandidates, type GisColumnMapping } from "../../model/projectEntities.ts";
import { geometryKey } from "../../model/gisLayer.ts";
import {
  loadGisFiles,
  chooseInitialIdField,
  geojsonFromLayerWithAttributes,
} from "../gisUpload.ts";
import { FileButton } from "../FileButton.tsx";
import { InfoIcon } from "../InfoIcon.tsx";
import { FieldsPreviewTable } from "./FieldsPreviewTable.tsx";

interface LinkableLayer {
  type: "FeatureCollection";
  linked: number;
  features: { geometry: GeoJSON.Geometry; properties: { attributes: GeoJSON.GeoJsonProperties } }[];
}

/**
 * Generic version of GisLayerPanel.tsx (same flow: upload a shapefile or
 * start from the already-loaded layer, choose the link column, preview,
 * and create as new the elements that don't link to any existing one)
 * for aquifers and outlet points.
 */
export function LinkGisPanel<T extends { gisCode: number | null }, C extends LinkableLayer>({
  entities,
  layer,
  link,
  onLinked,
  existingGeometries,
  onCreate,
  singularName,
  pluralName,
}: {
  entities: T[];
  layer: C | null;
  link: (geojson: GeoJSON.FeatureCollection, entities: T[], campoId: string) => C;
  onLinked: (layer: C) => void;
  /** Geometries already linked to a live project element — see geometriesOfLiveEntities (gisLayer.ts). */
  existingGeometries: ReadonlySet<string>;
  onCreate: (geojson: GeoJSON.FeatureCollection, mapping: GisColumnMapping) => void;
  singularName: string;
  pluralName: string;
}) {
  const [geojson, setGeojson] = useState<GeoJSON.FeatureCollection | null>(() =>
    layer && layer.features.length > 0 ? geojsonFromLayerWithAttributes(layer) : null
  );
  const [fieldId, setFieldId] = useState(() => {
    if (!layer || layer.features.length === 0) return "";
    const initialFc = geojsonFromLayerWithAttributes(layer);
    const { numericFields, textFields } = mappingCandidates(initialFc);
    return chooseInitialIdField(
      numericFields,
      textFields,
      (campo) => link(initialFc, entities, campo).linked
    );
  });
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [nameFieldToCreate, setNameFieldToCreate] = useState("");
  const [mode, setMode] = useState<"link" | "create">("link");
  const t = useT();

  async function onFiles(files: FileList | null) {
    if (!files) return;
    setError(null);
    setNotice(null);
    try {
      const { geojson: fc, notice: loadedNotice } = await loadGisFiles(files, t);
      setGeojson(fc);
      setNotice(loadedNotice);
      const { numericFields, textFields } = mappingCandidates(fc);
      setFieldId(
        chooseInitialIdField(numericFields, textFields, (campo) => link(fc, entities, campo).linked)
      );
      setNameFieldToCreate(textFields[0] ?? "");
    } catch (err) {
      // Same reasoning as GisLayerPanel.tsx's own catch — see its comment.
      console.error("Could not load GIS files:", err);
      setError(translateError(err, t));
      setGeojson(null);
    }
  }

  const previewLayer = geojson && fieldId ? link(geojson, entities, fieldId) : null;
  const { textFields: textFields, numericFields: numericFields } = geojson
    ? mappingCandidates(geojson)
    : { textFields: [], numericFields: [] };
  const availableFields = [...new Set([...numericFields, ...textFields])];
  const missingFeatures =
    geojson?.features.filter(
      (f) => !existingGeometries.has(geometryKey(f.geometry as GeoJSON.Geometry))
    ) ?? [];

  return (
    <div>
      <p>
        {layer && layer.features.length > 0
          ? t("gisLayerPanel.layerAlreadyExists")
          : t("linkGisPanel.uploadShapefileOf", { plural: pluralName })}
      </p>
      <FileButton multiple accept=".shp,.dbf,.prj,.shx,.cpg,.geojson,.json" onFiles={onFiles} />
      {error && <p className="error">{error}</p>}
      {notice && <p className="engine-notice">{notice}</p>}

      {geojson && (
        <div className="step-mapping">
          <div className="edit-panel-tabs">
            <button
              className={mode === "link" ? "tab-active" : undefined}
              onClick={() => setMode("link")}
            >
              {t("linkGisPanel.linkMode")}
            </button>
            <button
              className={mode === "create" ? "tab-active" : undefined}
              onClick={() => setMode("create")}
            >
              {t("linkGisPanel.createMode")}
              {missingFeatures.length > 0 && ` (${missingFeatures.length})`}
            </button>
          </div>

          {mode === "link" ? (
            <>
              <label>
                {t("gisLayerPanel.linkColumn")}
                <InfoIcon text={t("linkGisPanel.linkColumnHelp", { singular: singularName })} />
                <select value={fieldId} onChange={(e) => setFieldId(e.target.value)}>
                  {availableFields.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>
              {previewLayer && (
                <p>
                  {t("linkGisPanel.linkedCount", {
                    n: previewLayer.linked,
                    total: entities.length,
                    plural: pluralName,
                  })}
                </p>
              )}

              <FieldsPreviewTable
                geojson={geojson}
                fields={availableFields}
                chosenField={fieldId}
              />

              <button
                className="button button-primary"
                disabled={!previewLayer || previewLayer.linked === 0}
                onClick={() => previewLayer && onLinked(previewLayer)}
              >
                {t("gisLayerPanel.linkLayer")}
              </button>
            </>
          ) : missingFeatures.length === 0 ? (
            <p className="muted-text">
              {t("linkGisPanel.allAlreadyLinked", { singular: singularName })}
            </p>
          ) : (
            <>
              <p>
                {t("linkGisPanel.missingCount", {
                  n: missingFeatures.length,
                  total: geojson.features.length,
                  singular: singularName,
                  plural: pluralName,
                })}
              </p>

              <FieldsPreviewTable
                geojson={geojson}
                fields={availableFields}
                chosenField={nameFieldToCreate}
              />

              <label>
                {t("linkGisPanel.nameOf", { singular: singularName })}
                <select
                  value={nameFieldToCreate}
                  onChange={(e) => setNameFieldToCreate(e.target.value)}
                >
                  {textFields.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>
              <button
                className="button"
                disabled={!nameFieldToCreate}
                onClick={() => geojson && onCreate(geojson, { nameField: nameFieldToCreate })}
              >
                {t("linkGisPanel.createButton", {
                  n: missingFeatures.length,
                  name: missingFeatures.length === 1 ? singularName : pluralName,
                })}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
